/**
 * Service pour la gestion des favoris avec cache
 * Optimise les performances en évitant les appels API répétés
 */
class FavoriteService {
  constructor() {
    this.cache = new Map(); // Cache des favoris par utilisateur
    this.pendingRequests = new Map(); // Éviter les appels simultanés
  }

  /**
   * Vérifier si une recette est dans les favoris (avec cache)
   */
  async checkFavoriteStatus(recipeId, userId) {
    const cacheKey = `${userId}_${recipeId}`;

    // Retourner depuis le cache si disponible
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Éviter les appels simultanés
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const request = this._makeCheckRequest(recipeId, userId);
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      this.cache.set(cacheKey, result);
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Faire l'appel API pour vérifier le statut
   */
  async _makeCheckRequest(recipeId, userId) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.warn("Aucun token d'authentification trouvé");
        return false;
      }

      console.log(
        `🔍 Vérification favori - Recipe: ${recipeId}, User: ${userId}`
      );

      const response = await fetch(
        `http://localhost:3000/api/favorites/check/${recipeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log(`📡 Réponse API favoris: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Statut favori: ${data.isFavorite}`);
        return data.isFavorite;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ Erreur API favoris: ${response.status}`, errorData);
        return false;
      }
    } catch (error) {
      console.error("❌ Erreur lors de la vérification des favoris:", error);
      return false;
    }
  }

  /**
   * Ajouter une recette aux favoris
   */
  async addToFavorites(recipeId, userId) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }

      console.log(`➕ Ajout favori - Recipe: ${recipeId}, User: ${userId}`);

      const response = await fetch("http://localhost:3000/api/favorites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recipe_id: recipeId, user_id: userId }),
      });

      console.log(`📡 Réponse ajout favori: ${response.status}`);

      if (response.ok) {
        // Mettre à jour le cache
        const cacheKey = `${userId}_${recipeId}`;
        this.cache.set(cacheKey, true);
        console.log(`✅ Favori ajouté avec succès`);
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ Erreur ajout favori: ${response.status}`, errorData);
        throw new Error(
          errorData.message || "Erreur lors de l'ajout aux favoris"
        );
      }
    } catch (error) {
      console.error("❌ Erreur addToFavorites:", error);
      throw error;
    }
  }

  /**
   * Supprimer une recette des favoris
   */
  async removeFromFavorites(recipeId, userId) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }

      console.log(
        `➖ Suppression favori - Recipe: ${recipeId}, User: ${userId}`
      );

      const response = await fetch(
        `http://localhost:3000/api/favorites/${recipeId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log(`📡 Réponse suppression favori: ${response.status}`);

      if (response.ok) {
        // Mettre à jour le cache
        const cacheKey = `${userId}_${recipeId}`;
        this.cache.set(cacheKey, false);
        console.log(`✅ Favori supprimé avec succès`);
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          `❌ Erreur suppression favori: ${response.status}`,
          errorData
        );
        throw new Error(
          errorData.message || "Erreur lors de la suppression des favoris"
        );
      }
    } catch (error) {
      console.error("❌ Erreur removeFromFavorites:", error);
      throw error;
    }
  }

  /**
   * Basculer le statut favori
   */
  async toggleFavorite(recipeId, userId, currentStatus) {
    try {
      if (currentStatus) {
        await this.removeFromFavorites(recipeId, userId);
        return false;
      } else {
        await this.addToFavorites(recipeId, userId);
        return true;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Vider le cache
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Vider le cache pour un utilisateur spécifique
   */
  clearUserCache(userId) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${userId}_`)) {
        this.cache.delete(key);
      }
    }
  }
}

// Instance singleton
const favoriteService = new FavoriteService();
export default favoriteService;
